"use client";

import { useImport } from "@/context/importContext";
import {
  getImportFields,
  parseSourceText,
  resolveSource,
  type ImportFieldType,
  type ResolvedSourceType,
} from "@/utils/import/importSource";
import { useMemo } from "react";

/**
 * The data to import as the wizard currently reads it: the rows (pasted or from the
 * file), the profile fields, and whether the first row is a header (what the user
 * chose, or what the data suggests until they choose).
 */
export const useResolvedSource = (): {
  fields: ImportFieldType[];
  rawRows: string[][];
  hasHeader: boolean;
  resolved: ResolvedSourceType;
} => {
  const { importData } = useImport();
  const { selectedProfile, source } = importData;

  const fields = useMemo(() => getImportFields(selectedProfile.columns), [selectedProfile]);

  const rawRows = useMemo(
    () => (source.mode === "file" ? (source.fileRows ?? []) : parseSourceText(source.text)),
    [source.mode, source.fileRows, source.text],
  );

  return useMemo(() => {
    const suggested = resolveSource(rawRows, false, fields).suggestedHasHeader;
    const hasHeader = source.hasHeaderTouched ? source.hasHeader : suggested;
    return {
      fields,
      rawRows,
      hasHeader,
      resolved: resolveSource(rawRows, hasHeader, fields),
    };
  }, [rawRows, fields, source.hasHeader, source.hasHeaderTouched]);
};
